'use client'

import { useEffect, useRef } from 'react'

import type { Id as ToastId } from 'react-toastify'
import { toast } from 'react-toastify'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { ButtonSizes, showSnackbar } from '@b-stock/fe-core/bstock-react'

import { FeatureFlags } from '@/helpers/featureFlags'

import {
  hasDismissedNotificationOverlay,
  markNotificationOverlayDismissed,
} from './sessionDismissal'
import type { NotificationOverlayNotification } from './types'
import {
  ActionButton,
  ActionLinkButton,
  ActionRow,
  Message,
  StyledBanner,
} from './ui'

// A single, fixed group so a newer notification replaces (not stacks on top
// of) whatever this surface is currently showing, and so an already-active
// overlay just updates in place instead of flashing closed and reopening.
const TOAST_GROUP = 'notification-overlay'

export type NotificationOverlayProps = {
  /**
   * The single high-urgency notice to show, or `null`/`undefined` to show
   * none. This component only renders what it's given — deciding WHICH
   * notification to surface, and how often, is entirely the caller's job.
   */
  notification: NotificationOverlayNotification | null | undefined
  /**
   * Fired the moment the buyer dismisses this exact notification. The
   * session-scoped suppression (so it never reappears this session) happens
   * internally regardless of whether a caller provides this.
   */
  onDismiss?: (notification: NotificationOverlayNotification) => void
}

const NotificationOverlayContent = ({
  notification,
  onDismissClick,
}: {
  notification: NotificationOverlayNotification
  onDismissClick: () => void
}) => {
  const handleActionClick = notification.action?.onClick

  return (
    <StyledBanner
      type="warning"
      title={notification.title}
      onDismiss={onDismissClick}
    >
      {notification.message ? <Message>{notification.message}</Message> : null}
      {notification.action ? (
        <ActionRow>
          {notification.action.href ? (
            <ActionLinkButton
              appearance="primary"
              size={ButtonSizes.small}
              href={notification.action.href}
            >
              {notification.action.label}
            </ActionLinkButton>
          ) : (
            <ActionButton
              appearance="primary"
              size={ButtonSizes.small}
              onClick={handleActionClick}
            >
              {notification.action.label}
            </ActionButton>
          )}
        </ActionRow>
      ) : null}
    </StyledBanner>
  )
}

/**
 * Non-blocking, dismissible overlay for high-urgency notices (e.g. an outbid
 * alert in an auction's final minutes) — a second delivery surface alongside
 * the notification tray, built on top of the existing snackbar/toast system
 * (`showSnackbar`/`SnackbarContainer`) rather than a parallel modal. Renders
 * nothing while `enable-notification-center` is off, regardless of what a
 * caller passes in, so a flag-off buyer sees no behavior change even if an
 * upstream caller calls this incorrectly.
 *
 * Frequency-capping and transactional-vs-marketing content gating are
 * explicitly out of scope here — that's caller/policy-layer logic upstream
 * of this component.
 *
 * Placeholder visual treatment pending design review: a warning-styled
 * `Banner` inside a toast, with an optional CTA. No mockup existed for this
 * surface at implementation time.
 */
const NotificationOverlay = ({
  notification,
  onDismiss,
}: NotificationOverlayProps) => {
  const isEnabled = useFlag(FeatureFlags.ENABLE_NOTIFICATION_CENTER)
  const activeToastId = useRef<ToastId | null>(null)

  // Latest-value refs, updated every render. The effect below keys only on
  // `notification?.id` (not the object/callback themselves), so a caller
  // passing an inline object or callback — a fresh identity every render —
  // doesn't re-trigger showSnackbar()/sessionStorage reads on every parent
  // re-render and clobber `activeToastId` without dismissing the prior toast.
  const notificationRef = useRef(notification)
  notificationRef.current = notification
  const onDismissRef = useRef(onDismiss)
  onDismissRef.current = onDismiss

  useEffect(() => {
    const dismissActiveToast = () => {
      if (activeToastId.current !== null) {
        toast.dismiss(activeToastId.current)
        activeToastId.current = null
      }
    }

    const currentNotification = notificationRef.current

    if (!isEnabled || !currentNotification) {
      dismissActiveToast()
      return
    }

    if (hasDismissedNotificationOverlay(currentNotification.id)) {
      dismissActiveToast()
      return
    }

    const handleDismiss = () => {
      markNotificationOverlayDismissed(currentNotification.id)
      dismissActiveToast()
      onDismissRef.current?.(currentNotification)
    }

    activeToastId.current = showSnackbar({
      content: (
        <NotificationOverlayContent
          notification={currentNotification}
          onDismissClick={handleDismiss}
        />
      ),
      options: {
        group: TOAST_GROUP,
        // High-urgency by definition — stays up until the buyer acts on it
        // or dismisses it, rather than auto-fading like a confirmation toast.
        autoClose: false,
        // Banner renders its own dismiss control; a second close button from
        // the toast chrome would be redundant.
        closeButton: false,
        // Wider than the default toast — this surface carries a title, body
        // and CTA, not just a short confirmation line. See ui.ts.
        maxWidth: '26rem',
      },
    })
    // notification/onDismiss are read from refs above; keying on notification?.id
    // is intentional, see the ref comment above.
    // oxlint-disable-next-line react/exhaustive-deps
  }, [isEnabled, notification?.id])

  // Dismiss on unmount so this surface doesn't outlive the component that
  // owns it (e.g. a route-scoped mount, rather than a persistent app-shell one).
  useEffect(() => {
    return () => {
      if (activeToastId.current !== null) {
        toast.dismiss(activeToastId.current)
      }
    }
  }, [])

  // The visible UI renders into the SnackbarContainer's toast portal, not here.
  return null
}

export default NotificationOverlay
